export const OBSERVATION_SCHEMA_VERSION = 1 as const;

export type ObservationEntityType = 'artist'|'event'|'venue'|'tour'|'provider_identity'|'portfolio_position'|'watchlist_item';
export type ObservationAvailability = 'available'|'partial'|'stale';
export type ObservationProvider = 'ticketmaster'|'spotify'|'youtube'|'setlistfm'|'eventim'|'viagogo'|'portfolio'|'watchlist'|'valemato';
export type ObservationMetric =
  | 'event_status' | 'onsale_status' | 'ticket_availability' | 'primary_price_min' | 'primary_price_max'
  | 'spotify_popularity' | 'youtube_views' | 'youtube_subscribers' | 'setlist_activity'
  | 'intelligence_confidence' | 'risk_signal_severity' | 'valemato_score'
  | 'recommendation' | 'provider_status'
  | 'portfolio_remaining_quantity' | 'portfolio_sold_quantity' | 'portfolio_realized_profit' | 'portfolio_realized_roi' | 'portfolio_position_status'
  | 'secondary_market_price'
  | 'watchlist_priority' | 'watchlist_opportunity_value' | 'watchlist_risk_value';

export type ObservationValue =
  | {type:'number'; value:number}
  | {type:'integer'; value:number}
  | {type:'currency'; amount:number; currency:string}
  | {type:'percentage'; value:number}
  | {type:'count'; value:number}
  | {type:'boolean'; value:boolean}
  | {type:'categorical'; value:string};

export interface ObservationProvenance {
  providerId:string;
  canonicalEntityId:string;
  providerEntityId?:string;
  capability?:string;
  endpoint?:string;
  sourceObservedAt?:string;
  cacheStatus?:'hit'|'miss'|'stale'|'not_applicable';
  freshness?:'fresh'|'stale'|'unknown';
  correlationId?:string;
  confidenceSource?:string;
}
export interface HistoricalObservation {
  id:string;
  entityType:ObservationEntityType;
  entityId:string;
  metric:ObservationMetric;
  value:ObservationValue;
  unit:string;
  observedAt:string;
  recordedAt:string;
  source:string;
  provider:ObservationProvider;
  availability:ObservationAvailability;
  confidence?:number;
  provenance:ObservationProvenance;
  metadata?:Readonly<Record<string,string|number|boolean>>;
  schemaVersion:typeof OBSERVATION_SCHEMA_VERSION;
}

type Rule={types:ObservationValue['type'][];unit:string|RegExp;min?:number;max?:number;categories?:readonly string[]};
const status=['onsale','upcoming','offsale','cancelled','postponed','rescheduled','unknown'] as const;
export const observationMetricRegistry:Readonly<Record<ObservationMetric,Rule>>={
 event_status:{types:['categorical'],unit:'status',categories:status},onsale_status:{types:['categorical'],unit:'status',categories:status},ticket_availability:{types:['boolean'],unit:'boolean'},
 primary_price_min:{types:['currency'],unit:/^[A-Z]{3}$/},primary_price_max:{types:['currency'],unit:/^[A-Z]{3}$/},spotify_popularity:{types:['integer'],unit:'index_0_100',min:0,max:100},
 youtube_views:{types:['count'],unit:'views',min:0},youtube_subscribers:{types:['count'],unit:'subscribers',min:0},setlist_activity:{types:['count'],unit:'setlists',min:0},
 intelligence_confidence:{types:['percentage'],unit:'percent',min:0,max:100},risk_signal_severity:{types:['categorical'],unit:'severity',categories:['low','medium','high','critical']},valemato_score:{types:['number'],unit:'index_0_100',min:0,max:100},
 recommendation:{types:['categorical'],unit:'recommendation'},provider_status:{types:['categorical'],unit:'status'},
 portfolio_remaining_quantity:{types:['count'],unit:'tickets',min:0},portfolio_sold_quantity:{types:['count'],unit:'tickets',min:0},portfolio_realized_profit:{types:['currency'],unit:/^[A-Z]{3}$/},portfolio_realized_roi:{types:['percentage'],unit:'percent'},portfolio_position_status:{types:['categorical'],unit:'status',categories:['open','partial','sold','cancelled']},secondary_market_price:{types:['currency'],unit:/^[A-Z]{3}$/,min:0},
 watchlist_priority:{types:['categorical'],unit:'priority',categories:['low','medium','high','critical']},watchlist_opportunity_value:{types:['number'],unit:'index_0_100',min:0,max:100},watchlist_risk_value:{types:['number'],unit:'index_0_100',min:0,max:100}
};

const iso=(v:unknown):v is string=>typeof v==='string'&&v.length>0&&!Number.isNaN(Date.parse(v));
const finiteValue=(v:ObservationValue)=>v.type==='currency'?v.amount:typeof v.value==='number'?v.value:null;
export function isHistoricalObservation(v:unknown):v is HistoricalObservation {
 if(!v||typeof v!=='object')return false;const o=v as HistoricalObservation,r=observationMetricRegistry[o.metric];
 if(!r||o.schemaVersion!==1||typeof o.id!=='string'||!o.id||typeof o.entityId!=='string'||!o.entityId||typeof o.source!=='string'||!o.source||!iso(o.observedAt)||!iso(o.recordedAt)||!o.value||typeof o.value!=='object'||!r.types.includes(o.value.type)||!['available','partial','stale'].includes(o.availability))return false;
 if(!['artist','event','venue','tour','provider_identity','portfolio_position','watchlist_item'].includes(o.entityType)||!['ticketmaster','spotify','youtube','setlistfm','eventim','viagogo','portfolio','watchlist','valemato'].includes(o.provider))return false;
 if(o.provider==='viagogo'&&(o.metric!=='secondary_market_price'||o.entityType!=='portfolio_position'))return false;
 if(!o.provenance||o.provenance.providerId!==o.provider||o.provenance.canonicalEntityId!==o.entityId)return false;
 if(typeof r.unit==='string'?o.unit!==r.unit:!r.unit.test(o.unit))return false;
 if(o.value.type==='currency'&&o.value.currency!==o.unit)return false;
 const n=finiteValue(o.value);if(n!==null&&(!Number.isFinite(n)||(r.min!==undefined&&n<r.min)||(r.max!==undefined&&n>r.max)))return false;
 if(['integer','count'].includes(o.value.type)&&n!==null&&!Number.isInteger(n))return false;
 if(r.categories&&(!('value'in o.value)||typeof o.value.value!=='string'||!r.categories.includes(o.value.value)))return false;
 return o.confidence===undefined||(Number.isFinite(o.confidence)&&o.confidence>=0&&o.confidence<=100);
}

export function createHistoricalObservation(input:Omit<HistoricalObservation,'schemaVersion'>):HistoricalObservation|null {
 const candidate={...input,schemaVersion:OBSERVATION_SCHEMA_VERSION};
 return isHistoricalObservation(candidate)?candidate:null;
}
